import React, { useState, useEffect } from 'react'
import { XIcon } from 'lucide-react'
import { ContractJobTemplate, createJobTemplate, updateJobTemplate } from '../api/contractJobTemplates'

interface JobTemplateEditorProps {
  isOpen: boolean
  onClose: () => void
  contractId: string
  template?: ContractJobTemplate | null
  scheduledDate?: string
  onSave: () => void
}

export function JobTemplateEditor({
  isOpen,
  onClose,
  contractId,
  template,
  scheduledDate,
  onSave,
}: JobTemplateEditorProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    notes: '',
    priority: 'medium',
    scheduled_start_time: '',
    scheduled_end_time: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (template) {
      // Editing existing template
      setFormData({
        title: template.title || '',
        description: template.description || '',
        notes: template.notes || '',
        priority: template.priority || 'medium',
        scheduled_start_time: template.scheduled_start_time || '',
        scheduled_end_time: template.scheduled_end_time || '',
      })
    } else if (scheduledDate) {
      // Creating new template
      setFormData({
        title: '',
        description: '',
        notes: '',
        priority: 'medium',
        scheduled_start_time: '',
        scheduled_end_time: '',
      })
    }
  }, [template, scheduledDate, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      if (template) {
        // Update existing template
        await updateJobTemplate(template.id, {
          title: formData.title || undefined,
          description: formData.description || undefined,
          notes: formData.notes || undefined,
          priority: formData.priority,
          scheduled_start_time: formData.scheduled_start_time || undefined,
          scheduled_end_time: formData.scheduled_end_time || undefined,
        })
      } else if (scheduledDate) {
        // Create new template
        await createJobTemplate({
          contract_id: contractId,
          scheduled_date: scheduledDate,
          title: formData.title || undefined,
          description: formData.description || undefined,
          notes: formData.notes || undefined,
          priority: formData.priority,
          scheduled_start_time: formData.scheduled_start_time || undefined,
          scheduled_end_time: formData.scheduled_end_time || undefined,
        })
      }

      onSave()
      onClose()
    } catch (err) {
      console.error('Error saving template:', err)
      setError(err instanceof Error ? err.message : 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Scheduled Job' : 'Add Scheduled Job'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scheduled Date
            </label>
            <input
              type="date"
              value={template?.scheduled_date || scheduledDate || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              This job will be automatically created on this date
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Override default job title"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use contract title + date
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional job details"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="time"
                value={formData.scheduled_start_time}
                onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="time"
                value={formData.scheduled_end_time}
                onChange={(e) => setFormData({ ...formData, scheduled_end_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes for this specific job"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : template ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

